import { reducedStream, reducer } from '@ardoq/rxbeach';
import { qualitativeTheme } from '@ardoq/color-picker';
import {
  allDashboardColorThemesUnselected,
  colorThemeRequestFailed,
  createDashboardColorTheme,
  dashboardColorThemeSelected,
  deleteDashboardColorTheme,
  getDashboardColorThemes,
  selectDashboardColorTheme,
  setDashboardColorThemes,
  setIsDashboardColorThemesLoading,
  updateDashboardColorTheme,
  type FailedColorThemeRequest,
} from './actions';
import { APIDashboardColorTheme, ArdoqId } from '@ardoq/api-types';
import { setStateProperty } from '@ardoq/common-helpers';
import { map } from 'rxjs';

export const DEFAULT_DASHBOARD_COLOR_THEME = {
  isSelected: true,
  ...qualitativeTheme,
  label: 'Default qualitative theme',
  isDefaultTheme: true,
  isBuiltIn: true,
  _id: 'default',
};

export type DashboardColorThemes$State = {
  isLoading: boolean;
  dashboardColorThemes: APIDashboardColorTheme[];
  savedSelectedId: ArdoqId | null;
  failedRequest: FailedColorThemeRequest | null;
};

const defaultState: DashboardColorThemes$State = {
  isLoading: true,
  dashboardColorThemes: [],
  savedSelectedId: null,
  failedRequest: null,
};

const handleSetDashboardColorThemes = (
  state: DashboardColorThemes$State,
  dashboardColorThemes: APIDashboardColorTheme[]
): DashboardColorThemes$State => {
  return {
    ...state,
    dashboardColorThemes: dashboardColorThemes.sort((a, b) => {
      if (a.isBuiltIn !== b.isBuiltIn) return a.isBuiltIn ? -1 : 1;
      return a.created! < b.created! ? -1 : 1;
    }),
    savedSelectedId:
      dashboardColorThemes.find(({ isSelected }) => isSelected)?._id ??
      DEFAULT_DASHBOARD_COLOR_THEME._id,
  };
};

const handleSelectDashboardColorTheme = (
  state: DashboardColorThemes$State,
  selectedDashboardId: string
): DashboardColorThemes$State => ({
  ...state,
  dashboardColorThemes: state.dashboardColorThemes.map(dashboardColorTheme => ({
    ...dashboardColorTheme,
    isSelected: dashboardColorTheme._id === selectedDashboardId,
  })),
  failedRequest: null,
});

const clearFailedRequest = (
  state: DashboardColorThemes$State
): DashboardColorThemes$State => ({ ...state, failedRequest: null });

const dashboardColorThemes$ = reducedStream<DashboardColorThemes$State>(
  'dashboardColorThemes$',
  defaultState,
  [
    reducer(setDashboardColorThemes, handleSetDashboardColorThemes),
    reducer(selectDashboardColorTheme, handleSelectDashboardColorTheme),
    reducer(setIsDashboardColorThemesLoading, setStateProperty('isLoading')),
    reducer(colorThemeRequestFailed, setStateProperty('failedRequest')),
    reducer(getDashboardColorThemes, clearFailedRequest),
    reducer(createDashboardColorTheme, clearFailedRequest),
    reducer(updateDashboardColorTheme, clearFailedRequest),
    reducer(deleteDashboardColorTheme, clearFailedRequest),
    reducer(dashboardColorThemeSelected, clearFailedRequest),
    reducer(allDashboardColorThemesUnselected, clearFailedRequest),
  ]
);

export const selectedDashboardColorPalette$ = dashboardColorThemes$.pipe(
  map(
    ({ dashboardColorThemes }) =>
      dashboardColorThemes.find(({ isSelected }) => isSelected)
        ?.paletteColors ?? qualitativeTheme.paletteColors
  )
);

export { dashboardColorThemes$ };
